/**
 * Changing one food of a planned meal by hand (the diet plan's "Trocar alimento"): another food from the database in
 * its place, a new amount, or the food taken out. The database gives calories and macros per 100 g; the plan keeps
 * them for the amount eaten, as the AI writes them. No AI here.
 */
import { oneDecimal, roundAmount, weighedAmount } from './portions';

// The most the grams field takes, as in the calorie tracker's sheet
export const MAX_GRAMS = 5000;
// A food with almost no calories (lettuce, coffee) would need kilos to match: the grams offered stop here
const MAX_MATCHED_GRAMS = 1000;
const DEFAULT_GRAMS = 100;

/**
 * The grams of a database food (`per100` kcal in 100 g) that give `calories`, rounded the way the plan's portions are
 * (5 g steps, 10 g from 100 g). Without calories on either side it's 100 g.
 */
export const gramsToMatch = (per100, calories) => {
  if (!(per100 > 0) || !(calories > 0)) return DEFAULT_GRAMS;
  return Math.min(roundAmount((calories / per100) * 100), MAX_MATCHED_GRAMS);
};

/** A database food (values per 100 g) as a plan food of `grams`: "250g", its calories and macros for that much. */
export const planFoodFromSearch = (result, grams, name) => {
  const per = (value) => ((value || 0) * grams) / 100;
  return {
    name,
    quantity: `${grams}g`,
    calories: Math.round(per(result.calories)),
    protein: oneDecimal(per(result.protein)),
    carbs: oneDecimal(per(result.carbs)),
    fat: oneDecimal(per(result.fat)),
  };
};

/** A grams (or ml) field's text as an amount, or null when it isn't one the plan can hold. */
export const parseAmount = (text) => {
  const amount = Number(String(text ?? '').replace(',', '.'));
  return Number.isFinite(amount) && amount > 0 && amount <= MAX_GRAMS ? Math.round(amount) : null;
};

/** The amount slider's end: 500 g, or twice the amount in 500 g steps when that's more (1 litre of milk). */
export const sliderMaxFor = (amount) => Math.max(500, Math.ceil(((amount || 0) * 2) / 500) * 500);

/** Whether the food's amount can be changed by weight: "150g" and "200 ml" can, "2 unidades (100g)" can't. */
export const isWeighed = (food) => !!weighedAmount(food?.quantity);

/** The meal with its food at `index` replaced. */
export const replaceFoodAt = (meal, index, food) => ({ ...meal, foods: meal.foods.map((current, at) => (at === index ? food : current)) });

/** The meal without its food at `index`. */
export const removeFoodAt = (meal, index) => ({ ...meal, foods: meal.foods.filter((_, at) => at !== index) });

/** The meal's calories, rounded as the plan shows them. */
export const mealCalories = (foods) => Math.round((foods || []).reduce((total, food) => total + (food.calories || 0), 0));
